import { useOutletContext } from 'react-router-dom';
import {
  ListTodo, AlertCircle, CheckCircle2, Clock,
  ArrowRight, TrendingUp, MessageSquare
} from 'lucide-react';
import Card, { CardHeader } from '../../components/ui/Card';
import Badge from '../../components/ui/Badge';
import StatusBadge from '../../components/ui/StatusBadge';
import Button from '../../components/ui/Button';
import Avatar from '../../components/ui/Avatar';
import { formatMoneyCompact } from '../../utils/formatters';

const MOCK_MY_TASKS = [
  {
    id: 1,
    title: 'Verify INV-0449 cost correction in RO-507134',
    from: 'Nadeem Nisar',
    priority: 'high',
    dollars: 55,
    due: 'Today',
    description: 'INV-0449 shows $220 on statement but RO-507134 has it at $165. Need to correct ticket cost.',
    status: 'assigned',
  },
  {
    id: 2,
    title: 'Check INV-0451 price discrepancy',
    from: 'Nadeem Nisar',
    priority: 'high',
    dollars: 55,
    due: 'Today',
    description: 'INV-0451 is $220 but RO-507499 shows $165. Same issue as yesterday.',
    status: 'assigned',
  },
  {
    id: 3,
    title: 'Confirm core return: Helfman Ford #4095',
    from: 'Nadeem Nisar',
    priority: 'medium',
    dollars: 247,
    due: 'Apr 20',
    description: 'Check not showing on vendor cover page. Verify delivery to Helfman.',
    status: 'in_progress',
  },
  {
    id: 4,
    title: 'Missing invoice on RO-506320',
    from: 'Nadeem Nisar',
    priority: 'low',
    dollars: 89,
    due: 'Apr 22',
    description: 'Part on ticket but no PO. Please add PO or remove from ticket.',
    status: 'new',
  },
  {
    id: 5,
    title: 'Parts to Return pickup overdue: Transtar',
    from: 'Nadeem Nisar',
    priority: 'high',
    dollars: 489,
    due: 'Overdue',
    description: 'Torque converter has been sitting 9 days. Call Transtar for pickup.',
    status: 'assigned',
  },
];

export default function StoreUserDashboardPage() {
  const { dashboard, shopLabel } = useOutletContext();
  const today = new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' });
  const myOpenTasks = MOCK_MY_TASKS.filter((t) => t.status !== 'verified' && t.status !== 'resolved');
  const totalDollars = myOpenTasks.reduce((s, t) => s + t.dollars, 0);
  const overdue = myOpenTasks.filter((t) => t.due === 'Overdue').length;

  return (
    <div>
      {/* Welcome */}
      <div className="mb-6">
        <div className="text-[13px] text-[var(--color-warm-500)] mb-1">{today}</div>
        <h1 className="text-[24px] md:text-[26px] font-extrabold text-[var(--color-warm-900)] tracking-tight leading-tight">
          Hi Patricia, <span className="text-[var(--color-insight-600)]">{myOpenTasks.length} items</span> need you today.
        </h1>
        <p className="text-[13px] text-[var(--color-warm-600)] mt-1.5">
          {shopLabel} · {formatMoneyCompact(totalDollars)} total exposure
        </p>
      </div>

      {/* Metric row — 2 cols mobile, 3 cols desktop */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3 md:gap-4 mb-6 stagger-children">
        <MiniStat
          label="My open tasks"
          value={myOpenTasks.length}
          sub={overdue > 0 ? `${overdue} overdue` : 'On track'}
          accent={overdue > 0 ? 'danger' : 'action'}
          icon={<ListTodo />}
        />
        <MiniStat
          label="Shop exposure"
          value={formatMoneyCompact(totalDollars)}
          sub="across all tasks"
          accent="warm"
          icon={<TrendingUp />}
        />
        <MiniStat
          label="Resolved this week"
          value={8}
          sub="+3 from last week"
          accent="success"
          icon={<CheckCircle2 />}
          className="col-span-2 md:col-span-1"
        />
      </div>

      {/* Daily digest banner */}
      <Card className="mb-6 bg-gradient-to-br from-[var(--color-action-50)] to-[var(--color-cream-50)] border-[var(--color-action-100)]">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-full bg-[var(--color-action-100)] flex items-center justify-center text-[var(--color-action-600)] shrink-0">
            <MessageSquare size={18} />
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-[13px] font-bold text-[var(--color-warm-900)] mb-1">
              Daily note from Nadeem
            </div>
            <p className="text-[13px] text-[var(--color-warm-700)] leading-relaxed">
              Today's audit: 2 price mismatches on Advance Level invoices (INV-0449 & INV-0451) — same pattern, easy fix. Also a core return sitting past SLA from Transtar. Please prioritize these.
            </p>
            <div className="mt-2 text-[11px] text-[var(--color-warm-500)] flex items-center gap-1.5">
              <Clock size={11} />
              <span>Sent Apr 17 · 10:20 AM</span>
            </div>
          </div>
        </div>
      </Card>

      {/* My tasks list */}
      <Card padded={false}>
        <div className="p-5 pb-3">
          <CardHeader
            title={
              <span className="flex items-center gap-2.5">
                <ListTodo size={18} className="text-[var(--color-recovery-500)]" />
                My Tasks
                <Badge tone="neutral" size="xs">Preview</Badge>
              </span>
            }
            description={`${myOpenTasks.length} assigned to you. Tap to view, resolve, or add notes.`}
            action={<Button variant="ghost" size="sm" rightIcon={<ArrowRight size={14} />}>View all</Button>}
          />
        </div>
        <ul className="divide-y divide-[var(--color-warm-100)]">
          {MOCK_MY_TASKS.map((task) => (
            <TaskRow key={task.id} task={task} />
          ))}
        </ul>
      </Card>
    </div>
  );
}

function MiniStat({ label, value, sub, accent = 'warm', icon, className = '' }) {
  const accents = {
    warm:     'bg-[var(--color-cream-50)] border-[var(--color-warm-100)]',
    recovery: 'bg-[var(--color-recovery-50)] border-[var(--color-recovery-100)]',
    action:   'bg-[var(--color-action-50)] border-[var(--color-action-100)]',
    insight:  'bg-[var(--color-insight-50)] border-[var(--color-insight-100)]',
    danger:   'bg-[var(--color-danger-50)] border-[var(--color-danger-100)]',
    success:  'bg-[var(--color-success-50)] border-[var(--color-success-100)]',
  };
  const iconColors = {
    warm: 'text-[var(--color-warm-500)]',
    recovery: 'text-[var(--color-recovery-500)]',
    action: 'text-[var(--color-action-500)]',
    insight: 'text-[var(--color-insight-500)]',
    danger: 'text-[var(--color-danger-500)]',
    success: 'text-[var(--color-success-500)]',
  };
  return (
    <div className={`p-4 rounded-[var(--radius-lg)] border ${accents[accent]} ${className}`}>
      <div className={`flex items-center gap-2 mb-2 ${iconColors[accent]} [&_svg]:w-4 [&_svg]:h-4`}>
        {icon}
        <span className="text-[10px] font-bold uppercase tracking-[0.8px] text-[var(--color-warm-500)]">
          {label}
        </span>
      </div>
      <div className="text-[22px] md:text-[26px] font-extrabold text-[var(--color-warm-900)] tabular-nums leading-none">
        {value}
      </div>
      <div className="text-[11px] text-[var(--color-warm-600)] mt-1 tabular-nums">
        {sub}
      </div>
    </div>
  );
}

function TaskRow({ task }) {
  const priorityTone = task.priority === 'high' ? 'danger' : task.priority === 'medium' ? 'action' : 'neutral';
  const isOverdue = task.due === 'Overdue';
  return (
    <li className="px-5 py-4 hover:bg-[var(--color-cream-100)] transition-colors cursor-pointer">
      <div className="flex items-start gap-3">
        <div className={`w-1 h-16 rounded-full shrink-0 ${
          task.priority === 'high' ? 'bg-[var(--color-danger-500)]'
          : task.priority === 'medium' ? 'bg-[var(--color-action-500)]'
          : 'bg-[var(--color-warm-300)]'
        }`} />
        <div className="min-w-0 flex-1">
          <div className="flex items-start gap-2 flex-wrap mb-1">
            <span className="text-[14px] font-semibold text-[var(--color-warm-900)] leading-snug flex-1 min-w-0">
              {task.title}
            </span>
            <span className="text-[14px] font-bold tabular-nums text-[var(--color-warm-900)] shrink-0">
              ${task.dollars}
            </span>
          </div>
          <p className="text-[12px] text-[var(--color-warm-600)] mb-2.5 line-clamp-2">
            {task.description}
          </p>
          <div className="flex items-center gap-2 flex-wrap">
            <StatusBadge status={task.status} size="xs" />
            <Badge tone={priorityTone} size="xs">{task.priority} priority</Badge>
            <span className={`text-[11px] font-semibold ml-auto tabular-nums ${
              isOverdue ? 'text-[var(--color-danger-600)]' : 'text-[var(--color-warm-500)]'
            }`}>
              <Clock size={11} className="inline mr-0.5 -mt-0.5" />
              Due: {task.due}
            </span>
          </div>
        </div>
      </div>
    </li>
  );
}
